import { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useNavigation from '@/hooks/useNavigation';
import useResponse from '@/hooks/useResponse';
import routes from '@/pages/corporate/routes';
import {
  ArrayOperationCommandEnum,
  CreateFileInput,
  FileCategoryEnum,
  FileProjectStatusEnum,
  FilePurposeEnum,
  UpdateFileInput,
} from '@/types/schema';

import useFileMutations from '../useFileMutations';

function useFileActions({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();
  const { createFile, updateFile } = useFileMutations();
  const navigation = useNavigation();

  const createProject = useCallback(
    async (
      values: Omit<CreateFileInput, 'category' | 'purpose'>,
      shouldNavigateOnSuccess: boolean = true,
    ) => {
      const {
        name,
        description,
        dueDate,
        projectOwnerId,
        clientIds,
        iconSettings,
        media,
        visibilitySettings,
        stages,
        entity,
      } = values;
      setIsFetching(true);

      const data = await createFile({
        data: {
          name,
          description,
          dueDate,
          projectOwnerId,
          clientIds,
          iconSettings: iconSettings?.value ? iconSettings : undefined,
          media,
          visibilitySettings,
          stages,
          category: FileCategoryEnum.File,
          purpose: FilePurposeEnum.TaskCategory,
          projectStatus: FileProjectStatusEnum.Active,
          ...(entity ? { entity } : {}),
        },
      });

      handleResponse({
        condition: !!data.data?.createFile?.file?.id,
        onSuccess: () => {
          showSnackbar({
            message: t('Task:ProjectAdded'),
          });
          if (shouldNavigateOnSuccess) {
            navigation.push(routes.ProjectsDashboard(data.data?.createFile?.file?.id!), {
              appendQuery: false,
            });
          }
          onSuccess();
        },
      });

      setIsFetching(false);
    },
    [createFile, handleResponse, navigation, onSuccess, showSnackbar, t],
  );

  const updateProject = useCallback(
    async (fileId: string, values: Omit<UpdateFileInput, 'fileId' | 'category' | 'purpose'>) => {
      const {
        name,
        description,
        dueDate,
        projectOwnerId,
        iconSettings,
        media,
        visibilitySettings,
        clientIds,
        stages,
        relatedToEntities,
      } = values;
      setIsFetching(true);

      const data = await updateFile({
        data: {
          fileId,
          name,
          description,
          dueDate,
          projectOwnerId,
          clientIds,
          iconSettings: iconSettings?.value ? iconSettings : null,
          media,
          visibilitySettings,
          stages,
          category: FileCategoryEnum.File,
          relatedToEntities,
        },
      });

      handleResponse({
        condition: !data.error,
        onSuccess: () => {
          showSnackbar({
            message: t('Task:ProjectUpdated'),
          });
          onSuccess();
        },
      });

      setIsFetching(false);
    },
    [handleResponse, onSuccess, showSnackbar, t, updateFile],
  );

  const createList = useCallback(
    async (values: Omit<CreateFileInput, 'category' | 'purpose'>, folderId?: string) => {
      const { name, iconSettings, visibilitySettings } = values;
      setIsFetching(true);

      const data = await createFile({
        data: {
          name,
          iconSettings: iconSettings?.value ? iconSettings : undefined,
          visibilitySettings,
          category: FileCategoryEnum.File,
          purpose: FilePurposeEnum.TaskCategory,
        },
      });

      if (folderId) {
        updateFile({
          data: {
            fileId: folderId,
            children: {
              values: [data.data?.createFile?.file?.id!],
              operation: [ArrayOperationCommandEnum.Add],
              position: 0,
            },
          },
        });
      }

      handleResponse({
        condition: !!data.data?.createFile?.file?.id,
        onSuccess: () => {
          showSnackbar({
            message: t('Task:ProjectAdded'),
          });
          onSuccess();
        },
      });

      setIsFetching(false);

      return {
        listId: data.data?.createFile?.file?.id,
      };
    },
    [createFile, handleResponse, onSuccess, showSnackbar, t, updateFile],
  );

  const updateList = useCallback(
    async (fileId: string, values: Omit<UpdateFileInput, 'fileId' | 'category' | 'purpose'>) => {
      const { name, iconSettings, visibilitySettings } = values;
      setIsFetching(true);

      const data = await updateFile({
        data: {
          fileId,
          name,
          iconSettings: iconSettings?.value ? iconSettings : null,
          visibilitySettings,
          category: FileCategoryEnum.File,
        },
      });

      handleResponse({
        condition: !data.error,
        onSuccess: () => {
          showSnackbar({
            message: t('Task:ListUpdated'),
          });
          onSuccess();
        },
      });

      setIsFetching(false);
    },
    [handleResponse, onSuccess, showSnackbar, t, updateFile],
  );

  const createSection = useCallback(
    async (values: Omit<CreateFileInput, 'category' | 'purpose'>) => {
      const { name, iconSettings, visibilitySettings } = values;
      setIsFetching(true);

      const data = await createFile({
        data: {
          name,
          iconSettings: iconSettings?.value ? iconSettings : undefined,
          visibilitySettings,
          category: FileCategoryEnum.Folder,
          purpose: FilePurposeEnum.TaskCategory,
        },
      });

      handleResponse({
        condition: !!data.data?.createFile?.file?.id,
        onSuccess: () => {
          showSnackbar({
            message: t('Task:Section.SectionAdded'),
          });
          onSuccess();
        },
      });

      setIsFetching(false);
    },
    [createFile, handleResponse, onSuccess, showSnackbar, t],
  );

  const updateSection = useCallback(
    async (fileId: string, values: Omit<UpdateFileInput, 'fileId' | 'category' | 'purpose'>) => {
      const { name, iconSettings, visibilitySettings } = values;
      setIsFetching(true);

      const data = await updateFile({
        data: {
          fileId,
          name,
          iconSettings: iconSettings?.value ? iconSettings : undefined,
          visibilitySettings,
        },
      });

      handleResponse({
        condition: !data.error,
        onSuccess: () => {
          showSnackbar({
            message: t('Task:Section.SectionUpdated'),
          });
          onSuccess();
        },
      });

      setIsFetching(false);
    },
    [handleResponse, onSuccess, showSnackbar, t, updateFile],
  );

  const createSectionWithEmptyList = useCallback(
    async (values: Omit<CreateFileInput, 'category' | 'purpose'>) => {
      const { name, iconSettings, visibilitySettings } = values;
      setIsFetching(true);

      const listData = await createFile({
        data: {
          name: 'Untitled List',
          iconSettings: undefined,
          visibilitySettings,
          category: FileCategoryEnum.File,
          purpose: FilePurposeEnum.TaskCategory,
        },
      });

      const listId = listData.data?.createFile?.file?.id;

      if (listData.error || !listId) {
        setIsFetching(false);
        return;
      }

      const sectionData = await createFile({
        data: {
          name,
          iconSettings: iconSettings?.value ? iconSettings : undefined,
          visibilitySettings,
          category: FileCategoryEnum.Folder,
          purpose: FilePurposeEnum.TaskCategory,
        },
      });

      const sectionId = sectionData.data?.createFile?.file?.id;

      if (sectionData.error || !sectionId) {
        setIsFetching(false);
        return;
      }

      const data = await updateFile({
        data: {
          fileId: sectionId,
          children: {
            values: [listId],
            operation: [ArrayOperationCommandEnum.Add],
            position: 0,
          },
        },
      });

      handleResponse({
        condition: !!data.data?.updateFile?.file?.id,
        onSuccess: () => {
          showSnackbar({
            message: t('Task:Section.SectionAdded'),
          });
          onSuccess();
        },
      });

      setIsFetching(false);

      return {
        sectionId,
        listId,
      };
    },
    [createFile, handleResponse, onSuccess, showSnackbar, t, updateFile],
  );

  return {
    state: { isFetching },
    methods: {
      createProject,
      updateProject,
      createList,
      updateList,
      createSection,
      updateSection,
      createSectionWithEmptyList,
    },
  };
}

export default useFileActions;
